import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MusicIcon, MicIcon, GuitarIcon, RadioIcon, SparklesIcon, HeartIcon } from 'lucide-react';
import { members } from '../data/members';
import { useTheme } from '../context/ThemeContext';
import { useSEO } from '../hooks/useSEO';
import { StructuredData } from '../components/StructuredData';
const bandPhotoLight = 'https://cdn.magicpatterns.com/uploads/7Nb4VgUjgYiNyEG22BNfBK/ChatGPT_Image_May_13,_2026,_02_14_47_PM_(1).png';
const bandPhotoDark = 'https://cdn.magicpatterns.com/uploads/wA4mpXNHHeMoTu2j6wrFoL/ChatGPT_Image_May_13,_2026,_02_14_47_PM_(2).png';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
const genres = [{
  name: 'Pop & Charts',
  icon: MusicIcon,
  description: 'Aktuelle Hits und zeitlose Pop-Klassiker'
}, {
  name: 'Rock & Alternative',
  icon: GuitarIcon,
  description: 'Von Classic Rock bis Modern Alternative'
}, {
  name: 'Deutschsprachige Songs',
  icon: RadioIcon,
  description: 'Deutsche Hits mit Energie und Emotion'
}, {
  name: 'Balladen',
  icon: HeartIcon,
  description: 'Emotionale Momente für besondere Augenblicke'
}, {
  name: 'Funk & Groove',
  icon: SparklesIcon,
  description: 'Tanzbarer Groove und mitreißende Rhythmen'
}, {
  name: 'Eigene Produktionen',
  icon: MicIcon,
  description: 'Originale Songs mit eigenem Charakter'
}];
import { repertoire } from '../data/repertoire';
const previewSongs = repertoire.slice(0, 5);
export function Home() {
  const {
    theme
  } = useTheme();
  const bandPhoto = theme === 'dark' ? bandPhotoDark : bandPhotoLight;
  useSEO({
    title: 'Tuesday – Liveband & Coverband aus Kempten im Allgäu',
    description: 'Tuesday ist eine vielseitige Liveband aus Kempten im Allgäu. Pop, Rock, Alternative, Balladen und deutschsprachige Hits für Hochzeiten, Firmenevents, Bars und Festivals. Jetzt anfragen.',
    path: '/'
  });
  return <div className="w-full" data-id="element-131">
      <StructuredData id="musicgroup" data={{
      '@context': 'https://schema.org',
      '@type': 'MusicGroup',
      name: 'Tuesday',
      alternateName: 'Tuesday Band',
      genre: ['Pop', 'Rock', 'Alternative', 'Cover', 'Deutschsprachig', 'Funk', 'Ballade'],
      description: 'Liveband und Coverband aus Kempten im Allgäu für Hochzeiten, Firmenevents, Bars, Privatpartys und Festivals.',
      email: 'booking@tuesday-band.de',
      location: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Kempten',
          addressRegion: 'Allgäu',
          addressCountry: 'DE'
        }
      },
      areaServed: [{
        '@type': 'Place',
        name: 'Allgäu'
      }, {
        '@type': 'Place',
        name: 'Bayern'
      }, {
        '@type': 'Place',
        name: 'Süddeutschland'
      }],
      member: members.map(m => ({
        '@type': 'Person',
        name: m.name,
        roleName: m.role
      }))
    }} data-id="element-132" />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 sm:px-6 lg:px-8" data-id="element-133">
        <div className="max-w-4xl mx-auto text-center" data-id="element-134">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-display text-6xl sm:text-7xl md:text-8xl font-bold text-primary mb-6" data-id="element-135">
            Tuesday
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl sm:text-2xl text-primary mb-6 font-light" data-id="element-136">
            Liveband aus Kempten im Allgäu. Live-Covers, eigene Produktionen und
            ein bühnenfertiger Sound.
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="text-lg text-muted mb-8 max-w-2xl mx-auto" data-id="element-137">
            Tuesday verbindet modernen Pop, Rock, Alternative, Balladen,
            groovige Songs, deutschsprachige Hits und ausgewählte eigene
            Produktionen für flexible Live-Sets.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-138">
            <Link to="/buchen" className="px-8 py-3 bg-accent text-[var(--color-text-inv)] font-medium rounded hover:bg-[var(--color-accent-solid-hover)] transition-colors" data-id="element-139">
              Tuesday buchen
            </Link>
            <Link to="/repertoire" className="px-8 py-3 border-2 border-border-base text-primary font-medium rounded hover:border-accent hover:text-accent transition-colors" data-id="element-140">
              Zum Repertoire
            </Link>
          </motion.div>

          {/* Band Photo */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="mt-12 aspect-[4/3] sm:aspect-[16/10] rounded-lg overflow-hidden border border-border-base" data-id="element-141">
            <img src={bandPhoto} alt="Tuesday Band — live in der Bar" className="w-full h-full object-cover object-center" data-id="element-142" />
          </motion.div>
        </div>
      </section>

      {/* Band Intro */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-143">
        <motion.div {...fadeInUp} className="text-center" data-id="element-144">
          <p className="text-lg text-muted leading-relaxed" data-id="element-145">
            Tuesday ist eine vielseitige Liveband aus Kempten im Allgäu, die
            moderne Covers mit ausgewählten eigenen Produktionen kombiniert.
            Unser Sound ist bühnenerprobt, flexibel und auf verschiedene
            Event-Formate abgestimmt, von intimen Bar-Auftritten in Kempten bis
            zu größeren Festival-Sets in ganz Süddeutschland. Wir bringen
            Energie, Emotion und musikalische Vielfalt auf die Bühne.
          </p>
        </motion.div>
      </section>

      {/* Was wir spielen */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-146">
        <motion.h2 {...fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-primary text-center mb-12" data-id="element-147">
          Was wir spielen
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-148">
          {genres.map((genre, index) => <motion.div key={genre.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-surface border border-border-base rounded-lg p-6 hover:border-accent transition-colors" data-id="element-149">
              <genre.icon size={32} className="text-accent mb-4" data-id="element-150" />
              <h3 className="font-display text-xl font-semibold text-primary mb-2" data-id="element-151">
                {genre.name}
              </h3>
              <p className="text-muted text-sm" data-id="element-152">{genre.description}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Repertoire Preview */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-153">
        <motion.h2 {...fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-primary text-center mb-12" data-id="element-154">
          Repertoire-Vorschau
        </motion.h2>
        <div className="space-y-3 mb-8" data-id="element-155">
          {previewSongs.map((song, index) => <motion.div key={song.title} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-surface border border-border-base rounded-lg p-4 flex justify-between items-center hover:border-accent transition-colors" data-id="element-156">
              <div data-id="element-157">
                <h3 className="font-semibold text-primary" data-id="element-158">{song.title}</h3>
                <p className="text-muted text-sm" data-id="element-159">{song.artist}</p>
              </div>
              <MusicIcon size={20} className="text-accent opacity-50" data-id="element-160" />
            </motion.div>)}
        </div>
        <motion.div {...fadeInUp} className="text-center" data-id="element-161">
          <Link to="/repertoire" className="inline-block px-8 py-3 border-2 border-border-base text-primary font-medium rounded hover:border-accent hover:text-accent transition-colors" data-id="element-162">
            Zum vollständigen Repertoire
          </Link>
        </motion.div>
      </section>

      {/* Band Members */}
      <section id="mitglieder" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-163">
        <motion.h2 {...fadeInUp} className="font-display text-4xl sm:text-5xl font-bold text-primary text-center mb-12" data-id="element-164">
          Die Band
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-165">
          {members.map((member, index) => <motion.div key={member.slug} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-166">
              <Link to={`/mitglieder/${member.slug}`} className="block bg-surface border border-border-base rounded-lg overflow-hidden hover:border-accent transition-colors group" data-id="element-167">
                <div className="aspect-square bg-surface flex items-center justify-center overflow-hidden" data-id="element-168">
                  {(theme === 'dark' ? (member as any).imageDark : (member as any).imageLight) ? <img src={theme === 'dark' ? (member as any).imageDark : (member as any).imageLight} alt={`${member.name} — ${member.role}`} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" data-id="element-169" /> : <span className="font-display text-6xl text-primary opacity-20 group-hover:text-accent group-hover:opacity-30 transition-colors" data-id="element-170">
                      {member.imagePlaceholder}
                    </span>}
                </div>
                <div className="p-4" data-id="element-171">
                  <h3 className="font-display text-xl font-semibold text-primary mb-1" data-id="element-172">
                    {member.name}
                  </h3>
                  <p className="text-muted text-sm" data-id="element-173">{member.role}</p>
                </div>
              </Link>
            </motion.div>)}
        </div>
      </section>

      {/* Booking CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-174">
        <motion.div {...fadeInUp} className="bg-[var(--color-secondary-hover)] border border-secondary rounded-lg p-8 sm:p-12 text-center" data-id="element-175">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary mb-4" data-id="element-176">
            Ihr sucht eine Liveband im Allgäu?
          </h2>
          <p className="text-muted text-lg mb-6 max-w-2xl mx-auto" data-id="element-177">
            Tuesday ist buchbar für Hochzeiten, Bars, Privatveranstaltungen,
            Firmenevents, Kulturfestivals und kleine Festivals in Kempten, dem
            Allgäu und der gesamten Region. Wir passen unsere Sets individuell
            an eure Bedürfnisse an.
          </p>
          <Link to="/buchen" className="inline-block px-8 py-3 bg-accent text-[var(--color-text-inv)] font-medium rounded hover:bg-[var(--color-accent-solid-hover)] transition-colors" data-id="element-178">
            Tuesday buchen
          </Link>
        </motion.div>
      </section>
    </div>;
}